"use client";
import { HeroCursorIcon } from "@/assets/icons/MainPageIcon";
import { cn } from "@/lib/utils/utils";
import { motion, useMotionValue, useSpring, useTransform } from "framer-motion";
import { useEffect } from "react";

interface HeroCursorProps {
  label: string;
  color: string;
  className?: string;
  rangeX?: [number, number];
  rangeY?: [number, number];
}

const HeroCursor = ({
  label,
  color,
  className,
  rangeX = [-40, 40],
  rangeY = [-30, 30],
}: HeroCursorProps) => {
  //Raw motion values to track mouse percentage (-0.5 to 0.5)
  const x = useMotionValue(0);
  const y = useMotionValue(0);

  //Wrap those raw values in a 'Spring' for that smooth, premium following effect
  const moveX = useSpring(x, { stiffness: 100, damping: 20 });
  const moveY = useSpring(y, { stiffness: 100, damping: 20 });

  //Transform the small percentage (-0.5 to 0.5) into actual pixels
  const translateX = useTransform(moveX, [-0.5, 0.5], rangeX);
  const translateY = useTransform(moveY, [-0.5, 0.5], rangeY);

  useEffect(() => {
    // Function to calculate mouse position relative to the center of the screen
    const updatePosition = (clientX: number, clientY: number) => {
      // clientX / innerWidth gives a range of 0 to 1.
      // Subtracting 0.5 centers it: (-0.5 is left, 0 is center, 0.5 is right)
      const xPosition = clientX / window.innerWidth - 0.5;
      const yPosition = clientY / window.innerHeight - 0.5;
      // Update the motion values (Does NOT trigger a React re-render)
      x.set(xPosition);
      y.set(yPosition);
    };
    const handleMouseMove = (e: MouseEvent) => {
      updatePosition(e.clientX, e.clientY);
    };
    const handleTouchMove = (e: TouchEvent) => {
      //Using the first touch point for single-finger interaction
      if (e.touches[0]) {
        updatePosition(e.touches[0].clientX, e.touches[0].clientY);
      }
    };
    window.addEventListener("mousemove", handleMouseMove);
    // Listeners for Mobile & MD (Touch)
    window.addEventListener("touchmove", handleTouchMove, { passive: true });
    window.addEventListener("touchstart", handleTouchMove, { passive: true });
    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("touchmove", handleTouchMove);
      window.removeEventListener("touchstart", handleTouchMove);
    };
  }, [x, y]);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      style={{ x: translateX, y: translateY }}
      transition={{ delay: 0.5 }}
      className={cn("absolute flex flex-col items-start z-20", className)}
    >
      <HeroCursorIcon
        color={color}
        className="md:w-6 md:h-6 w-4 h-4 rotate-[-15deg]"
      />
      <span
        className="px-1 md:px-2 xl:px-4 md:py-0.5 xl:py-2 text-[10px] lg:text-base font-medium capitalize tracking-widest text-black shadow-sm"
        style={{ backgroundColor: color }}
      >
        {label}
      </span>
    </motion.div>
  );
};

export default HeroCursor;
